import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEpisodes } from '@contexts/EpisodesContext';
import { useAudioPlayer } from '@contexts/AudioPlayerContext';
import { findEpisodeById } from '@models/episodes/episodeModel';
import { formatFullDate } from '@utils/time/dateUtils';
import { ROUTES } from '@constants/routes';
import { useTranscriptController } from './useTranscriptController';

export function useEpisodeDetailPageController() {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const { episodes, loading: episodesLoading } = useEpisodes();
  const { loadEpisode, currentEpisode } = useAudioPlayer();
  const [searchQuery, setSearchQuery] = useState('');
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const episode = findEpisodeById(episodes, episodeId);
  const transcriptState = useTranscriptController(episode?.transcriptUrl);

  useEffect(() => {
    setSearchQuery('');
    setTranscriptExpanded(false);
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [episodeId]);

  useEffect(() => {
    if (!episode) return;

    if (!currentEpisode || currentEpisode.url !== episode.url) {
      loadEpisode(episode);
    }
  }, [episode, currentEpisode, loadEpisode]);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleTranscriptToggle = useCallback(() => {
    setTranscriptExpanded((isExpanded) => !isExpanded);
  }, []);

  const handleBack = useCallback(() => {
    navigate(ROUTES.ALL_EPISODES);
  }, [navigate]);

  return {
    episode,
    episodesLoading,
    episodeDate: episode ? formatFullDate(episode.pubDate) : '',
    searchQuery,
    onSearchChange: handleSearchChange,
    transcriptExpanded,
    onTranscriptToggle: handleTranscriptToggle,
    onBack: handleBack,
    ...transcriptState
  };
}
